import { CopyOutlined, DeleteOutlined, EditOutlined, WarningFilled } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CaregiverProfileModal from 'components/modals/CaregiverProfileModal';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';
import { updateConversationLastMessageIfMatch } from 'hooks/conversationListMutators';
import { conversationMessagesQueryKey, conversationsQueryKey } from 'hooks/useConversations';
import { useMessageContextMenu } from 'hooks/useMessageContextMenu';
import useSelectConversations from 'hooks/useSelectConversations';
import { useSwipeToReply } from 'hooks/useSwipeToReply';
import posthog from 'posthog-js';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiPatientConversationsManager from 'services/api/ApiPatientConversationsManager';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { UserDTO } from 'services/api/models/UserDTO';
import { conversationActions } from 'store/conversations';
import { selectCurrentUser } from 'store/users/selectors';
import ExampleQuestions from './ExampleQuestions';
import MessageBubble from './MessageBubble';

export type MessagesListProps = {
  messages: ConversationMessage[];
  showExampleQuestions?: boolean;
  /** When false, swipe-to-reply is disabled (e.g. read-only historic conversations). */
  swipeToReplyEnabled?: boolean;
  /** Message list index (0-based) of the active search match to scroll to and flash-highlight. */
  activeSearchMatchMessageIndex?: number | null;
};

export type MessagesListHandle = {
  scrollToMessageIndex: (messageIndex: number) => void;
};

/** The DOM handle of each message, shared with the swipe and context-menu hooks and the test suite. */
export const MESSAGE_SELECTOR = '[data-slot=message]';
export const MESSAGE_BUBBLE_SELECTOR = '[data-slot=message-bubble]';

const contextMenuItemClassName = 'flex cursor-pointer items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-background';

/** A brief brightness pulse on a bubble, brighter for the user's own, darker for the others'. */
function flashBubble(bubble: HTMLElement, isMine: boolean, onDone?: () => void) {
  bubble.style.transition = 'filter 0.35s ease';
  bubble.style.filter = isMine ? 'brightness(1.08)' : 'brightness(0.94)';
  return setTimeout(() => {
    bubble.style.filter = 'brightness(1)';
    onDone?.();
  }, 700);
}

function MessagesListInner(props: MessagesListProps, ref: React.ForwardedRef<MessagesListHandle>) {
  const { messages, showExampleQuestions, swipeToReplyEnabled = true, activeSearchMatchMessageIndex } = props;

  // Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Selectors
  const currentUser = useSelector(selectCurrentUser);
  const { conversationMetadata, conversationId } = useSelectConversations();

  // State
  const [caregiverIdDisplayedInModal, setCaregiverIdDisplayedInModal] = useState(undefined as string | undefined);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editText, setEditText] = useState('');
  /** Link a caregiver is about to open, awaiting confirmation (`null` = no dialog). */
  const [linkToConfirm, setLinkToConfirm] = useState<string | null>(null);
  /** Message the user is about to delete, awaiting confirmation (`null` = no dialog). */
  const [messageIdToDelete, setMessageIdToDelete] = useState<number | null>(null);

  // Scroll management refs
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hasInitialScrolledRef = useRef(false);
  const previousMessageCountRef = useRef(0);
  const searchHighlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchHighlightBubbleRef = useRef<HTMLElement | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Callback ref to capture the parent scroll container
  const setContainerRef = (element: HTMLDivElement | null) => {
    containerRef.current = element;
    if (element) {
      // Parent element (from ConversationPage) is the actual scroll container
      scrollContainerRef.current = element.parentElement as HTMLDivElement;
    }
  };

  const messageElements = () => Array.from(containerRef.current?.querySelectorAll<HTMLElement>(MESSAGE_SELECTOR) ?? []);

  // Context menu — gesture detection only; action logic lives below
  const {
    contextMenu,
    contextMenuRef,
    closeContextMenu,
    handleContextMenu,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useMessageContextMenu({ containerRef, messagesRef, currentUser: currentUser ?? null });

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: async ({ messageId, messageText }: { messageId: number; messageText: string }) => {
      if (!conversationId) throw new Error('No conversation');
      if (currentUser?.is_caregiver) {
        await ApiDoctorConversationsManager.editMessage(conversationId, messageId, messageText);
      } else {
        await ApiPatientConversationsManager.editMessage(conversationId, messageId, messageText);
      }
    },
    onSuccess: (_, { messageId, messageText }) => {
      const editedAt = new Date().toISOString();
      queryClient.setQueryData<ConversationMessage[]>(
        conversationMessagesQueryKey(conversationId ?? ''),
        prev =>
          prev?.map(m => (m.id === messageId ? { ...m, message_text: messageText, edited_at: editedAt } : m)) ?? prev,
      );
      if (conversationId) {
        queryClient.setQueryData(
          conversationsQueryKey,
          updateConversationLastMessageIfMatch(conversationId, messageId, {
            message_text: messageText,
            edited_at: editedAt,
          }),
        );
      }
      posthog.capture('chats.message_edited');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (messageId: number) => {
      if (!conversationId) throw new Error('No conversation');
      if (currentUser?.is_caregiver) {
        await ApiDoctorConversationsManager.deleteMessage(conversationId, messageId);
      } else {
        await ApiPatientConversationsManager.deleteMessage(conversationId, messageId);
      }
    },
    onSuccess: (_, messageId) => {
      queryClient.setQueryData<ConversationMessage[]>(
        conversationMessagesQueryKey(conversationId ?? ''),
        prev =>
          prev?.map(m => {
            if (m.id === messageId) return { ...m, is_deleted: true };
            if (m.replied_to_message?.id === messageId)
              return { ...m, replied_to_message: { ...m.replied_to_message, is_deleted: true } };
            return m;
          }) ?? prev,
      );
      if (conversationId) {
        queryClient.setQueryData(
          conversationsQueryKey,
          updateConversationLastMessageIfMatch(conversationId, messageId, {
            message_text: '',
            is_deleted: true,
          }),
        );
      }
      posthog.capture('chats.message_deleted');
    },
  });

  // Track which message ID is being edited (set when the modal opens)
  const editingMessageIdRef = useRef<number | null>(null);

  const handleEditClickWithRef = useCallback(() => {
    if (!contextMenu.message) return;
    editingMessageIdRef.current = contextMenu.message.id;
    setEditText(contextMenu.message.message_text);
    setEditModalVisible(true);
    closeContextMenu();
  }, [contextMenu.message, closeContextMenu]);

  function handleCopyClick() {
    if (!contextMenu.message) return;
    navigator.clipboard.writeText(contextMenu.message.message_text).catch(() => {});
    closeContextMenu();
  }

  function handleDeleteClick() {
    if (!contextMenu.message) return;
    const messageId = contextMenu.message.id;
    closeContextMenu();
    setMessageIdToDelete(messageId);
  }

  function handleDeleteConfirm() {
    if (messageIdToDelete !== null) {
      deleteMutation.mutate(messageIdToDelete);
    }
    setMessageIdToDelete(null);
  }

  function handleOpenLinkConfirm() {
    if (linkToConfirm) {
      window.open(linkToConfirm, '_blank', 'noopener,noreferrer');
    }
    setLinkToConfirm(null);
  }

  function handleEditSubmit() {
    const messageId = editingMessageIdRef.current;
    if (!messageId) return;
    editMutation.mutate({ messageId, messageText: editText });
    setEditModalVisible(false);
  }

  useImperativeHandle(ref, () => ({
    scrollToMessageIndex: (messageIndex: number) => {
      messageElements()[messageIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  }));

  /**
   * When the active search match index changes: scroll that bubble into view and run the same
   * brief brightness pulse used for reply-reference taps. Does not depend on `messages` identity
   * (parent rebuilds the array each render) so typing does not re-trigger the flash.
   */
  useEffect(() => {
    if (searchHighlightTimeoutRef.current) {
      clearTimeout(searchHighlightTimeoutRef.current);
      searchHighlightTimeoutRef.current = null;
    }
    if (lastSearchHighlightBubbleRef.current) {
      lastSearchHighlightBubbleRef.current.style.filter = 'brightness(1)';
      lastSearchHighlightBubbleRef.current.style.transition = '';
      lastSearchHighlightBubbleRef.current = null;
    }

    if (activeSearchMatchMessageIndex == null || activeSearchMatchMessageIndex < 0) {
      return;
    }

    const messageElement = messageElements()[activeSearchMatchMessageIndex];
    messageElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const bubble = messageElement?.querySelector<HTMLElement>(MESSAGE_BUBBLE_SELECTOR);
    if (!bubble) return;

    const originalMessage = messagesRef.current[activeSearchMatchMessageIndex];
    const isMine = originalMessage?.sender?.id === currentUser?.id;
    lastSearchHighlightBubbleRef.current = bubble;
    searchHighlightTimeoutRef.current = flashBubble(bubble, isMine, () => {
      searchHighlightTimeoutRef.current = null;
      if (lastSearchHighlightBubbleRef.current === bubble) {
        lastSearchHighlightBubbleRef.current = null;
      }
    });
    // Intentionally only `activeSearchMatchMessageIndex`: parent passes a new `messages` array every render;
    // including it would re-flash on every keystroke while the match index stays the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [activeSearchMatchMessageIndex]);

  /** Clear any in-progress search highlight timer and reset filter styles on unmount. */
  useEffect(() => {
    return () => {
      if (searchHighlightTimeoutRef.current) {
        clearTimeout(searchHighlightTimeoutRef.current);
        searchHighlightTimeoutRef.current = null;
      }
      if (lastSearchHighlightBubbleRef.current) {
        lastSearchHighlightBubbleRef.current.style.filter = 'brightness(1)';
        lastSearchHighlightBubbleRef.current.style.transition = '';
        lastSearchHighlightBubbleRef.current = null;
      }
    };
  }, []);

  /**
   * Scroll behavior:
   * 1. On first render with messages: Instantly scroll to bottom
   * 2. On new message: Smoothly scroll the last message into view (any scroll position)
   */
  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const container = scrollContainerRef.current;
    const messageCountIncreased = messages.length > previousMessageCountRef.current;
    const isFirstRender = !hasInitialScrolledRef.current && messages.length > 0;

    if (isFirstRender) {
      // Initial scroll to bottom - instant, no animation (GYN-225)
      const maxScroll = container.scrollHeight - container.clientHeight;
      container.scrollTop = maxScroll;
      hasInitialScrolledRef.current = true;
    } else if (messageCountIncreased) {
      // New message: after paint, smooth-scroll last bubble into view
      requestAnimationFrame(() => {
        const elements = messageElements();
        elements[elements.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    }

    previousMessageCountRef.current = messages.length;
  }, [messages]);

  function setReplyingToMessageByIndex(messageIndex: number) {
    const originalMessage = messages[messageIndex];

    if (originalMessage && conversationId) {
      dispatch(
        conversationActions.setReplyingToMessage({
          conversationId,
          replyingTo: {
            messageIndex,
            messageId: originalMessage.id,
            messageText: originalMessage.message_text,
            senderName: originalMessage.sender.display_name,
          },
        }),
      );
    }
  }

  // Swipe-to-reply gesture handling (disabled in read-only views such as historic conversations)
  useSwipeToReply({
    messages,
    onReply: setReplyingToMessageByIndex,
    enabled: swipeToReplyEnabled,
  });

  function closeCaregiverProfileModal() {
    setCaregiverIdDisplayedInModal(undefined);
  }

  /** A tap on a quoted message scrolls to the original and flashes it. */
  function handleReplyClick(repliedMessageId: number) {
    const replyMessageIndex = messages.findIndex(m => m.id === repliedMessageId);
    if (replyMessageIndex < 0) return;
    const messageElement = messageElements()[replyMessageIndex];
    if (!messageElement) return;
    messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const bubble = messageElement.querySelector<HTMLElement>(MESSAGE_BUBBLE_SELECTOR);
    if (bubble) {
      flashBubble(bubble, messages[replyMessageIndex].sender.id === currentUser?.id);
    }
  }

  function handleTitleClick(senderId: string) {
    const sender = messages.find(m => m.sender?.id === senderId)?.sender;
    if (sender?.is_caregiver && !currentUser?.is_caregiver) {
      setCaregiverIdDisplayedInModal(senderId);
    }
  }

  return (
    <div
      ref={setContainerRef}
      className="flex flex-1 flex-col justify-start bg-background"
      data-swipe-to-reply={swipeToReplyEnabled ? 'true' : 'false'}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {caregiverIdDisplayedInModal && (
        <CaregiverProfileModal
          caregiverId={caregiverIdDisplayedInModal}
          open
          onClose={() => closeCaregiverProfileModal()}
        />
      )}
      <div data-slot="message-list" className="flex flex-col gap-2.5 py-5">
        {currentUser &&
          messages.map(message => (
            <MessageBubble
              key={message.id}
              message={message}
              currentUser={currentUser as UserDTO}
              conversationSummary={conversationMetadata}
              onTitleClick={handleTitleClick}
              onReplyClick={handleReplyClick}
              // Caregivers confirm before opening a link shared by a patient.
              onLinkClick={currentUser.is_caregiver ? setLinkToConfirm : undefined}
            />
          ))}
      </div>
      {showExampleQuestions && <ExampleQuestions />}

      {/* Floating context menu for copy/edit/delete */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="fixed z-[9999] min-w-40 rounded-md bg-card py-1 shadow-md"
          style={
            contextMenu.right !== undefined
              ? { right: contextMenu.right, top: contextMenu.y }
              : { left: contextMenu.x, top: contextMenu.y }
          }
          onClick={e => e.stopPropagation()}
        >
          <div className={contextMenuItemClassName} onClick={handleCopyClick}>
            <CopyOutlined />
            {t('chats.copyMessage')}
          </div>
          {/* Edit and delete are only available for the current user's messages */}
          {contextMenu.message?.sender.id === currentUser?.id && (
            <>
              <div className={contextMenuItemClassName} onClick={handleEditClickWithRef}>
                <EditOutlined />
                {t('chats.editMessage')}
              </div>
              <div className={`${contextMenuItemClassName} text-destructive-text`} onClick={handleDeleteClick}>
                <DeleteOutlined />
                {t('chats.deleteMessage.action')}
              </div>
            </>
          )}
        </div>
      )}

      {/* Edit message modal */}
      <Dialog open={editModalVisible} onOpenChange={open => !open && setEditModalVisible(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('chats.editMessage')}</DialogTitle>
          </DialogHeader>
          <Textarea
            value={editText}
            onChange={e => setEditText(e.target.value)}
            rows={3}
            className="max-h-56"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditSubmit} loading={editMutation.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete message confirmation */}
      <Dialog open={messageIdToDelete !== null} onOpenChange={open => !open && setMessageIdToDelete(null)}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WarningFilled className="text-warning" />
              {t('chats.deleteMessage.title')}
            </DialogTitle>
            <DialogDescription>{t('chats.deleteMessage.content')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageIdToDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              {t('chats.deleteMessage.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* External link confirmation (caregivers opening a link shared by a patient) */}
      <Dialog open={linkToConfirm !== null} onOpenChange={open => !open && setLinkToConfirm(null)}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WarningFilled className="text-warning" />
              {t('chats.linkConfirmation.title')}
            </DialogTitle>
            <DialogDescription className="break-all">
              {t('chats.linkConfirmation.content', { url: linkToConfirm ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkToConfirm(null)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleOpenLinkConfirm}>{t('chats.linkConfirmation.open')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MessagesList = forwardRef(MessagesListInner);
MessagesList.displayName = 'MessagesList';
export default MessagesList;
